import React from 'react';
import styles from './jobs_list.module.scss';
import classNames from 'classnames';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import Tooltip from '../../../components/react/tooltip/tooltip';
import { STAGE_OPTIONS } from '../../../utilities/constants/job_related.constants';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { sentenceCase } from 'change-case';
import format from 'date-fns/format';
import { Job } from '../../../job_search/types';
import ErrorBoundary from '../../../components/react/error_boundary/error_boundary';
import {
  EVENT_NAME_PROP,
  JOBS_SECTION_PROP,
  VIEW_SINGLE_RESULT_INTENTION,
} from '../../constants/global_search.constants';
const CLOUDINARY_BASE_URL = 'https://res.cloudinary.com/devex/image/fetch/c_pad,h_100,w_100,f_auto,q_auto';

const TRACKING = [EVENT_NAME_PROP, VIEW_SINGLE_RESULT_INTENTION, JOBS_SECTION_PROP].join(' ');

interface Props {
  results: Job[];
  isFetching: boolean;
}

const JobsList: React.FC<Props> = ({ results, isFetching }) => {
  const renderSingleJobPlaceholder = (
    <>
      <div className='col-lg-2 col-md-3 d-none d-md-block'>
        <div className='placeholder-glow'>
          <p className={`placeholder w-100 ${styles.imgPlaceholder}`}></p>
        </div>
      </div>
      <div className='col-lg-10 col-md-9 col-12 mt-48 mt-md-2'>
        <div className='placeholder-glow'>
          <p className='placeholder w-100 placeholder-lg' />
          <p className='placeholder w-100 placeholder-lg' />
          <p className='placeholder w-75' />
        </div>
      </div>
    </>
  );
  const renderLoadingPlaceholder = (
    <div data-testid='loading-placeholder' className='row'>
      {renderSingleJobPlaceholder}
      {renderSingleJobPlaceholder}
      {renderSingleJobPlaceholder}
    </div>
  );

  const renderSingleJob = (job: Job) => {
    const {
      name,
      display_logo,
      is_featured,
      published_at,
      slug_and_id,
      employer_company,
      places,
      is_remote,
      stage,
      closing_date,
    } = job;

    const newlyPublished = differenceInCalendarDays(new Date(), new Date(published_at)) <= 1;
    const notRecentlyPublished = differenceInCalendarDays(new Date(), new Date(published_at)) > 30;
    const stageOption = STAGE_OPTIONS.find((option) => option.value === sentenceCase(stage || ''));

    return (
      <div className='row py-35 border-bottom'>
        <div className='col-md-3 col-lg-2 d-none d-md-block'>
          <div className='border py-4 px-2 text-center'>
            {display_logo ? (
              <img
                alt='Company logo'
                src={`${CLOUDINARY_BASE_URL}/${display_logo}`}
              />
            ) : (
              <div className='text-gray-light my-25 fs-2 lh-1'>
                <i className='fal icon-building' />
              </div>
            )}
          </div>
          <div className='text-center mt-2'>
            {is_featured ? (
              <div className={`${styles.featuredTag} fw-bold`}>Featured</div>
            ) : (
              <div
                data-testid='published-date-section'
                className={classNames(`${styles.publishedDate}`, {
                  [styles.new]: newlyPublished,
                  [styles.lighter]: notRecentlyPublished,
                })}
              >
                {newlyPublished
                  ? 'New'
                  : formatDistanceToNow(new Date(published_at), { addSuffix: true })}
              </div>
            )}
          </div>
        </div>
        <div className='col-12 col-lg-10 col-md-9 mt-25' data-testid='job-info-container'>
          <a
            className={classNames('link-blue-orange', TRACKING)}
            href={`/jobs/${slug_and_id}`}
          >
            <h2 className='fs-6 fw-bold'>{name}</h2>
          </a>
          <div className='mt-2 mt-md-28'>
            {employer_company?.name && <span>{employer_company?.name}</span>}
            {Boolean(places.length) && employer_company?.name && <span> · </span>}
            {Boolean(places.length) && (
              <span className='text-gray-li' data-testid='job-places-section'>
                <span data-testid='job-places-value'>
                  {places[0]?.place_name}
                  {places.length > 1 ? ' ... ' : ''}
                </span>
                {is_remote && <span className='ms-1'>(Remote position)</span>}
              </span>
            )}
          </div>
          <div className='small text-gray mt-25 mt-md-35 d-flex'>
            {new Date(closing_date).toString() !== 'Invalid Date' && (
              <div className='' data-testid='closing-date'>
                <span
                  className={classNames({
                    'text-orange-dark':
                      differenceInCalendarDays(new Date(closing_date), new Date()) <= 7,
                  })}
                >
                  <i className='fal icon-clock text-gray-li' />
                  <span className='ms-1'>
                    Closing on {format(new Date(closing_date), 'MMM d, yyyy')}
                  </span>
                </span>
              </div>
            )}
            {stage && stageOption && (
              <div className='ms-5'>
                <Tooltip placement='top' message={stageOption?.tooltip || ''}>
                  <span className='pt-1'>
                    <i className='fal icon-info-circle text-gray-li' />
                    <span className='ms-1'>{stageOption?.value}</span>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderResults = (
    <div data-testid='jobs-list'>
      {results.slice(0, 3).map((job) => (
        <ErrorBoundary key={job.id}>{renderSingleJob(job)}</ErrorBoundary>
      ))}
    </div>
  );

  return isFetching ? renderLoadingPlaceholder : renderResults;
};

export default JobsList;
